/**
 * 操作履歴に出す言葉（運用される方が読む名前）。
 *
 * 操作の種類そのものの定義元は server/src/audit/actions.ts。ここはその**表示名**だけを持つ。
 * 表に無い種類が来ても画面は壊さず、「そのほかの操作」と出す（英字の記号を画面に出さない）。
 * 呼び方は決定30（トーク／メッセージ／既読・未読）と日本語監査の統一表にそろえてある。
 */
import type { AuditTarget } from '../api/types.js';

const ACTION_LABELS: Record<string, string> = {
  'intake.created': '自動メッセージの受け口を作った',
  'intake.updated': '自動メッセージの受け口を変えた',
  'intake.rotated': '自動メッセージの宛先を作り直した',
  'intake.deleted': '自動メッセージの受け口を消した',
  'user.deactivated': 'アカウントを無効にした',
  'user.reactivated': '無効を解除した',
  'user.role_changed': '役職を変えた',
  'user.site_changed': '所属拠点を変えた',
  'user.name_kana_changed': 'ふりがなを変えた',
  'invite.issued': '招待リンクを発行した',
  'invite.used': '招待リンクから使い始めた',
  'user.activated': 'はじめてログインした',
  'user.signed_in_again': 'ログインした（無効の解除のあと）',
  'join_request.approved': '参加の申し込みを承認した',
  'join_request.approved_as_existing':
    '参加の申し込みをメンバーの方として承認した（電話番号を差し替えた）',
  'join_request.rejected': '参加の申し込みを断った',
  'roster.imported': 'CSVを取り込んだ',
  'channel.created': 'トークを作った',
  'channel.updated': 'トークを変更した',
  'channel.made_dormant': 'トークを休眠にした',
  'channel.dormancy_lifted': 'トークの休眠を解いた',
  'channel.deleted': 'グループを削除した',
  'channel.restored': 'グループを元に戻した',
  'channel.purged': 'グループを完全に消した',
  'ai_knowledge.created': 'AI先輩の答えを登録した',
  'ai_knowledge.updated': 'AI先輩の答えを直した',
  'ai_knowledge.approved': 'AI先輩の答えを承認した',
  'ai_knowledge.disabled': 'AI先輩の答えを取り下げた',
  'announcement.published': 'お知らせを送った',
  'announcement.repushed': '未読の方へ再通知した',
  'data.exported': '会社のデータをまとめて取り出した',
  'data.export_downloaded': '取り出したデータを受け取った',
  // ご本人が自分の1対1を受け取った（issue #458）。どのトークか・お相手は記録に無い（1対1は管理者も読めない）
  'own_talk.export_downloaded': 'ご自分の1対1のトーク履歴を受け取った',
  // お知らせで使用中。トークを対象にした過去の記録の表示名としても残す（issue #534）
  'read_receipts.exported': '既読・未読の一覧を取り出した',
  'channel.evidence_changed': '「事故対応」の指定を変えた',
  'retention.changed': '保存期間を変えた',
  'admin.signed_in': '管理画面にログインした',
  'app.signed_in': 'アプリにログインした',
  'invite.signed_in': '招待リンクからログインした',
  'verification_token.signed_in': '検証用の鍵でログインした',
  // ストアの審査用の入口（決まった電話番号と認証番号）から入った（issue #536・審査用の会社の中だけ）
  'store_review.signed_in': 'ストアの審査用の入口からログインした',
  'login.limit_reached': 'ログインの上限に達した',
  'login.limit_raised': 'ログインの上限を一時的に引き上げた',
  'login.limit_raise_cancelled': 'ログインの上限の引き上げをやめた',
  'login.counts_reset': 'ログインの間違いの数を数え直した',
  'verification_sms.viewed': '認証番号の控えを見た',
  'sticker.registered': 'スタンプを取り込んだ',
  'sticker.updated': 'スタンプの名前・並び順を変えた',
  'sticker.disabled': 'スタンプを取り下げた',
  'sticker.enabled': 'スタンプを元に戻した',
  'sticker.moved': 'スタンプを別のセットへ移した',
  'sticker_set.created': 'スタンプのセットを作った',
  'sticker_set.renamed': 'スタンプのセットの名前を変えた',
  'sticker_set.reordered': 'スタンプのセットを並べ替えた',
  'sticker_set.deleted': 'スタンプのセットを消した',
  'setting.changed': '会社の設定を変えた',
  'role.changed': '役職のできることを変えた',
  'site.created': '拠点を足した',
  'site.renamed': '拠点の名前を変えた',
};

/** 表に無い操作のときの言葉（英字の記号をそのまま出さない）。 */
export const UNKNOWN_ACTION_LABEL = 'そのほかの操作';

/**
 * 操作の名前。
 *
 * 「AI先輩に教える」トークのメッセージは、書いた方が送るとそのまま承認待ちに入ります。
 * 記録の上では「答えの登録」と同じ種類ですが、そのまま「AI先輩の答えを登録した」と出すと、
 * 現場の方が答えを登録したように読めるため、言い分ける（総点検 J-07）。
 */
export function auditActionLabel(action: string, target?: AuditTarget | null): string {
  if (action === 'ai_knowledge.created' && target?.to === 'taught') {
    return '「AI先輩に教える」に送ったメッセージが承認待ちに入った';
  }
  return ACTION_LABELS[action] ?? UNKNOWN_ACTION_LABEL;
}

/** 対象の種別の言い換え。 */
const TARGET_TYPE_LABELS: Record<string, string> = {
  intake: '自動メッセージの受け口',
  user: 'メンバー',
  channel: 'トーク',
  announcement: 'お知らせ',
  ai_knowledge: 'AI先輩の答え',
  invite: '招待リンク',
  join_request: '参加の申し込み',
  roster_import: 'CSVの取り込み',
  organization: '会社全体',
  export: '取り出し',
  sticker: 'スタンプ',
  sticker_set: 'スタンプのセット',
  setting: '会社の設定',
  role: '役職',
  site: '拠点',
};

export function auditTargetTypeLabel(type: string | undefined): string {
  if (!type) return '';
  return TARGET_TYPE_LABELS[type] ?? '';
}
